import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Html5Qrcode } from 'html5-qrcode';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Camera, KeyboardIcon, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * QrPickupScanner
 * Opens the device camera, scans a QR code and looks up the matching order.
 * Falls back to manual token entry when the camera is unavailable.
 */
const QrPickupScanner = ({ open, onOpenChange }) => {
  const { getAuthHeader } = useAuth();
  const scannerRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [mode, setMode] = useState('camera'); // "camera" | "manual"
  const [manualToken, setManualToken] = useState('');
  const [order, setOrder] = useState(null);
  const [lookupError, setLookupError] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const lookup = async (token) => {
    setLookupError(null);
    try {
      const res = await axios.get(`${API}/matches/by-token/${encodeURIComponent(token)}`, {
        headers: { Authorization: getAuthHeader() }
      });
      setOrder(res.data);
    } catch (err) {
      setLookupError(err.response?.data?.detail || 'No se pudo leer el pedido');
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch (_) {}
      try { scannerRef.current.clear(); } catch (_) {}
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const startScanner = async () => {
    setLookupError(null);
    setOrder(null);
    if (scannerRef.current) return;
    setScanning(true);
    // Give React one tick to mount the reader div
    await new Promise((r) => setTimeout(r, 50));
    const el = document.getElementById('qr-reader-region');
    if (!el) { setScanning(false); return; }
    // Prefer facingMode; if it fails, try enumerating cameras and pick the rear
    const tryStart = async (source) => {
      const instance = new Html5Qrcode('qr-reader-region');
      scannerRef.current = instance;
      await instance.start(
        source,
        { fps: 10, qrbox: { width: 240, height: 240 } },
        async (decodedText) => {
          await stopScanner();
          await lookup(decodedText);
        },
        () => { /* per-frame decode errors ignored */ }
      );
    };
    try {
      await tryStart({ facingMode: { ideal: 'environment' } });
    } catch (err1) {
      try {
        const cams = await Html5Qrcode.getCameras();
        if (cams && cams.length) {
          // Prefer a rear-facing camera if labelled, else first
          const rear = cams.find((c) => /back|rear|environment/i.test(c.label || '')) || cams[0];
          await tryStart(rear.id);
        } else {
          throw err1;
        }
      } catch (err2) {
        setScanning(false);
        scannerRef.current = null;
        const msg = /permission|denied|notallowed/i.test(String(err2))
          ? 'Permiso de cámara denegado. Habilítalo en el navegador y vuelve a intentarlo.'
          : 'No se pudo acceder a la cámara. Introduce el código manualmente.';
        toast.error(msg);
        setLookupError(msg);
      }
    }
  };

  useEffect(() => {
    // Do not auto-start the camera. Many browsers (esp. mobile) require an
    // explicit user gesture to grant camera permission. The user hits the
    // "Iniciar cámara" button below to trigger startScanner().
    return undefined;
  }, [open, mode]);

  useEffect(() => {
    if (!open) {
      stopScanner();
      setOrder(null);
      setManualToken('');
      setLookupError(null);
      setMode('camera');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!manualToken.trim()) return;
    await lookup(manualToken.trim());
  };

  const markPickedUp = async () => {
    if (!order) return;
    setConfirming(true);
    try {
      await axios.post(`${API}/matches/${order.match_id}/mark-picked-up`, {}, {
        headers: { Authorization: getAuthHeader() }
      });
      toast.success('Pedido marcado como entregado');
      setOrder({ ...order, picked_up: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo marcar como entregado');
    } finally {
      setConfirming(false);
    }
  };

  const rescan = async () => {
    await stopScanner();
    setOrder(null);
    setLookupError(null);
    setManualToken('');
    if (mode === 'camera') startScanner();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="qr-scanner-dialog" className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Escanear código de recogida</DialogTitle>
          <DialogDescription>
            Enfoca el QR del comprador o introduce el código a mano.
          </DialogDescription>
        </DialogHeader>

        {!order ? (
          <>
            <div className="flex gap-2 mb-3">
              <Button
                type="button"
                variant={mode === 'camera' ? 'default' : 'outline'}
                onClick={() => { setMode('camera'); setLookupError(null); }}
                data-testid="scanner-mode-camera"
                className={`flex-1 ${mode === 'camera' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
              >
                <Camera size={14} className="mr-1" /> Cámara
              </Button>
              <Button
                type="button"
                variant={mode === 'manual' ? 'default' : 'outline'}
                onClick={async () => { await stopScanner(); setMode('manual'); setLookupError(null); }}
                data-testid="scanner-mode-manual"
                className={`flex-1 ${mode === 'manual' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
              >
                <KeyboardIcon size={14} className="mr-1" /> Manual
              </Button>
            </div>

            {mode === 'camera' ? (
              <div className="space-y-3">
                <div
                  id="qr-reader-region"
                  data-testid="qr-reader-region"
                  className="rounded-lg overflow-hidden bg-slate-950 min-h-[260px] flex items-center justify-center text-white text-sm"
                >
                  {!scanning && 'Pulsa "Iniciar cámara" para escanear'}
                </div>
                {!scanning ? (
                  <Button
                    type="button"
                    data-testid="start-camera-button"
                    onClick={startScanner}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    <Camera size={14} className="mr-1" /> Iniciar cámara
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="stop-camera-button"
                    onClick={stopScanner}
                    className="w-full"
                  >
                    Detener
                  </Button>
                )}
                <p className="text-xs text-slate-500 text-center">
                  Autoriza el acceso a la cámara cuando el navegador lo pida.
                </p>
                {lookupError && (
                  <p className="text-xs text-red-600 text-center" data-testid="camera-error">{lookupError}</p>
                )}
              </div>
            ) : (
              <form onSubmit={handleManualSubmit} className="space-y-3">
                <Input
                  data-testid="manual-token-input"
                  value={manualToken}
                  onChange={(e) => setManualToken(e.target.value.toUpperCase())}
                  placeholder="Ej: 4A2B1C3D"
                  className="font-mono uppercase text-center text-lg tracking-widest"
                />
                <Button
                  type="submit"
                  data-testid="manual-token-submit"
                  disabled={!manualToken.trim()}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  Buscar pedido
                </Button>
              </form>
            )}

            {lookupError && (
              <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800 flex items-start gap-2" data-testid="scanner-error">
                <XCircle size={16} className="mt-0.5 flex-shrink-0" />
                <span>{lookupError}</span>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-3" data-testid="scanned-order-details">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <p className="text-xs text-emerald-700 mb-1">Código válido</p>
              <p className="font-mono font-bold text-lg text-emerald-900" data-testid="scanned-token">
                {order.pickup_token}
              </p>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <p className="text-xs text-slate-500 mb-1">Comprador</p>
              <p className="text-sm font-semibold text-slate-900">{order.buyer_name}</p>
              <p className="text-sm text-slate-700">📞 {order.buyer_phone}</p>
              {order.buyer_email && <p className="text-sm text-slate-700">✉️ {order.buyer_email}</p>}
            </div>

            <div className="border border-slate-200 rounded-lg p-3">
              <p className="text-xs text-slate-500 mb-2">
                Pedido
                {order.type === 'catalog_order' && ' (catálogo)'}
                {order.type === 'surplus' && ' (excedente)'}
                {order.type === 'auction' && ' (subasta)'}
              </p>
              {order.type === 'catalog_order' ? (
                <ul className="space-y-1 text-sm" data-testid="scanned-items-list">
                  {(order.items || []).map((it, i) => (
                    <li key={i}>
                      <strong>{it.quantity}×</strong> {it.product} <span className="text-slate-500">({it.format})</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm">
                  <strong>{order.quantity}×</strong> {order.product} <span className="text-slate-500">({order.format})</span>
                  {order.agreed_price_per_unit != null && (
                    <span className="block text-xs text-slate-500 mt-1">
                      Precio acordado: €{Number(order.agreed_price_per_unit).toFixed(2)} / {order.format}
                    </span>
                  )}
                </p>
              )}
            </div>

            {order.picked_up && (
              <div className="rounded-lg bg-emerald-100 border border-emerald-300 text-emerald-900 p-3 text-sm flex items-center gap-2" data-testid="pickup-confirmed-badge">
                <CheckCircle2 size={16} /> Entregado
              </div>
            )}

            {order.cancelled && (
              <div className="rounded-lg bg-red-100 border border-red-300 text-red-900 p-3 text-sm flex items-center gap-2" data-testid="pickup-cancelled-badge">
                <XCircle size={16} /> Pedido cancelado{order.cancelled_by === 'buyer' ? ' por el comprador' : order.cancelled_by === 'seller' ? ' por ti' : ''}
              </div>
            )}

            <div className="flex gap-2">
              <Button variant="outline" onClick={rescan} className="flex-1" data-testid="scan-again-button">
                Escanear otro
              </Button>
              {!order.picked_up && !order.cancelled && (
                <Button
                  onClick={markPickedUp}
                  disabled={confirming}
                  data-testid="mark-picked-up-button"
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {confirming ? 'Guardando…' : 'Marcar entregado'}
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QrPickupScanner;
